/**
 * Récapitulatif des heures supplémentaires pour la clôture
 * @module components/heures-sup/HeuresSupRecap
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Clock, Plus, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

import HeuresSupModal from './HeuresSupModal';
import { useHeuresSup } from '@/hooks/useHeuresSup';
import type { HeuresSupSettings, CreateHeureSupData } from '@/types/heures-sup.types';
import { formatHeures, formatCoutHeuresSup } from '@/lib/calculs-heures-sup';

interface HeuresSupRecapProps {
  chantierId: string;
  entrepriseId: string;
  membresAffectes: Array<{
    id: string;
    nom: string;
    prenom: string;
    taux_horaire: number;
  }>;
  settings: HeuresSupSettings;
  onAddHeuresSup?: () => void;
  compact?: boolean;
}

export default function HeuresSupRecap({
  chantierId,
  entrepriseId,
  membresAffectes,
  settings,
  compact = false,
}: HeuresSupRecapProps) {
  // Si les heures sup ne sont pas activées, ne rien afficher
  if (!settings.heures_sup_actives) {
    return null;
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const {
    heuresSup,
    isLoading,
    totalHeures,
    totalCout,
    heuresParMembre,
    ajouterHeureSup,
    refetch,
  } = useHeuresSup(chantierId);

  // Sauvegarder une nouvelle entrée
  const handleSave = async (data: CreateHeureSupData) => {
    await ajouterHeureSup(data);
    setModalOpen(false);
    // Rester en mode ajout si l'utilisateur veut continuer
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // Version compacte (pour affichage dans d'autres contextes)
  if (compact) {
    return (
      <div className="flex items-center justify-between p-4 border rounded-lg bg-amber-500/5 border-amber-500/20">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-500" />
          <div>
            <div className="font-medium">Heures supplémentaires</div>
            <div className="text-sm text-muted-foreground">
              {heuresSup.length > 0
                ? `${formatHeures(totalHeures)} • ${formatCoutHeuresSup(totalCout)}`
                : 'Aucune heure sup enregistrée'}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500/10 rounded-lg">
          <Clock className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h3 className="font-semibold">Récapitulatif des heures supplémentaires</h3>
          <p className="text-sm text-muted-foreground">
            Vérifiez les heures sup avant de clôturer le chantier
          </p>
        </div>
      </div>

      {/* Tableau récap par membre */}
      {heuresParMembre.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Membre</TableHead>
                <TableHead className="text-right">Heures totales</TableHead>
                <TableHead className="text-right">Coût total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {heuresParMembre.map((membre) => (
                <TableRow key={membre.membre_id}>
                  <TableCell className="font-medium">
                    {membre.membre_prenom} {membre.membre_nom}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatHeures(membre.total_heures)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCoutHeuresSup(membre.total_cout)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Ligne total */}
              <TableRow className="bg-muted/50 font-semibold">
                <TableCell>TOTAL</TableCell>
                <TableCell className="text-right font-mono">
                  {formatHeures(totalHeures)}
                </TableCell>
                <TableCell className="text-right text-amber-500">
                  {formatCoutHeuresSup(totalCout)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-6 border rounded-lg bg-muted/30">
          <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">
            Aucune heure supplémentaire enregistrée pour ce chantier
          </p>
        </div>
      )}

      {/* Question et boutons */}
      <div className="p-4 border rounded-lg bg-amber-500/5 border-amber-500/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-3">
            <p className="font-medium">
              Avez-vous des heures supplémentaires oubliées à ajouter ?
            </p>
            <div className="flex gap-3">
              <Button
                variant="default"
                onClick={() => setModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Oui, ajouter des heures sup
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Fermer le mode ajout
                  setShowAddForm(false);
                }}
              >
                Non, continuer la clôture
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'ajout */}
      <HeuresSupModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        chantierId={chantierId}
        entrepriseId={entrepriseId}
        membresAffectes={membresAffectes}
        settings={settings}
        onSave={handleSave}
      />
    </div>
  );
}
